import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QuoteModal } from "@/components/QuoteModal";
import { NavLink } from "@/components/NavLink";
import { Leaf, Droplets, Recycle, Sun, Wind, TreePine, Globe, Target, Award, CheckCircle, Ship, Truck, Building } from "lucide-react";
import heroPort from "@/assets/hero-port.jpg";
import globalNetwork from "@/assets/global-network.jpg";
import warehouse from "@/assets/warehouse.jpg";

const Sustainability = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const initiatives = [
    {
      icon: Ship,
      title: "Clean Shipping Program",
      desc: "Partnering with carriers using low-sulfur fuels and modern, fuel-efficient vessels to reduce maritime emissions."
    },
    {
      icon: Truck,
      title: "Green Fleet",
      desc: "Our road transport partners are transitioning to electric and hybrid vehicles for last-mile deliveries."
    },
    {
      icon: Building,
      title: "Eco-Friendly Warehouses",
      desc: "Solar-powered facilities in California, USA with rainwater harvesting and LED lighting systems."
    },
    {
      icon: Recycle,
      title: "Packaging Reduction",
      desc: "Working with clients to minimize packaging waste and promote recyclable materials throughout the supply chain."
    },
    {
      icon: TreePine,
      title: "Carbon Offset Program",
      desc: "Every shipment can be carbon-neutral through our partnership with verified reforestation projects."
    },
    {
      icon: Droplets,
      title: "Water Conservation",
      desc: "Our facilities in Athens and Bangkok implement water recycling and conservation measures."
    }
  ];

  const goals = [
    { year: "2025", goal: "50% reduction in operational carbon emissions", progress: 75 },
    { year: "2027", goal: "100% renewable energy in all owned facilities", progress: 60 },
    { year: "2030", goal: "Carbon-neutral operations across all services", progress: 40 },
    { year: "2035", goal: "Zero waste to landfill", progress: 25 }
  ];

  const certifications = [
    { name: "ISO 14001", desc: "Environmental Management System certification for our operations." },
    { name: "EcoVadis Gold", desc: "Recognized for our sustainability performance in the logistics sector." },
    { name: "SmartWay Partner", desc: "Committed to improving supply chain fuel efficiency and reducing emissions." },
    { name: "Green Marine", desc: "Participant in the North American environmental certification program." }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src={heroPort}
          alt="Sustainable shipping operations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-maritime-dark/80" />
        <div className="relative z-10 text-center text-primary-foreground px-4">
          <Leaf className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Sustainability</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Committed to sustainable logistics from Athens to Bangkok and beyond. Building a greener future for global trade.
          </p>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Our Commitment */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Our Commitment to the Planet</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  At Costal Meridian, we believe that sustainable business practices and environmental responsibility are not just good ethics—they're good business. Operating from our headquarters in California, USA, we're uniquely positioned to lead sustainability efforts in our global trade operations.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  We're proud to offer carbon-neutral shipping options, eco-friendly packaging solutions, and transparent environmental reporting to all our clients in the USA and worldwide.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">ISO 14001 Certified</span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                    <Award className="h-5 w-5" />
                    <span className="text-sm font-medium">EcoVadis Gold</span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src={globalNetwork}
                  alt="Costal Meridian sustainable operations"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Key Initiatives */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-4">Environmental Initiatives</h2>
            <p className="text-xl text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Implementing sustainable practices at every step of our global network.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {initiatives.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="hover-scale border-2 hover:border-green-500">
                    <CardContent className="p-8">
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Sustainability Goals */}
          <section className="mb-20 bg-secondary rounded-xl p-12">
            <div className="text-center mb-12">
              <Target className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-4xl font-bold mb-4">Our Sustainability Roadmap</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Clear targets and measurable progress toward a sustainable future for logistics.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {goals.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-primary">{item.year}</span>
                      <span className="text-sm font-medium text-green-600">{item.progress}% Complete</span>
                    </div>
                    <p className="text-muted-foreground mb-4">{item.goal}</p>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full transition-all"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Regional Efforts */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Operational Sustainability Efforts</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="hover-scale">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <Globe className="h-10 w-10 text-primary" />
                    <div>
                      <h3 className="text-2xl font-bold">USA Operations</h3>
                      <p className="text-muted-foreground">Corporate HQ & Facilities</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Solar panels covering 80% of energy needs at our primary warehouse</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Electric vehicle integration for local customer service operations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Partnership with local ports for shore power connections</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Rainwater harvesting system collecting 500,000 liters annually</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="hover-scale">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <Globe className="h-10 w-10 text-primary" />
                    <div>
                      <h3 className="text-2xl font-bold">Global Partner Network</h3>
                      <p className="text-muted-foreground">Worldwide Logistics</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Selecting partners with eco-performance certifications on all routes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Carbon-offset options available for ocean shipping cargo</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>100% paperless billing and automated digital trade document compliance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <span>Continuous reduction of flight carbon footprint on air cargo lines</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Certifications */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Environmental Certifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {certifications.map((cert, idx) => (
                <Card key={idx} className="text-center hover-scale">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{cert.name}</h3>
                    <p className="text-sm text-muted-foreground">{cert.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Green Services */}
          <section className="mb-20 bg-gradient-to-r from-green-900 to-green-700 rounded-xl p-12 text-primary-foreground">
            <div className="text-center mb-12">
              <Sun className="h-12 w-12 mx-auto mb-4" />
              <h2 className="text-4xl font-bold mb-4">Green Shipping Options</h2>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Choose sustainable shipping solutions for your cargo at no extra cost.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <Wind className="h-10 w-10 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Carbon Offset</h3>
                <p className="opacity-80">Automatically offset your shipment's carbon footprint through verified projects.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <Recycle className="h-10 w-10 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Eco Packaging</h3>
                <p className="opacity-80">Recyclable and biodegradable packaging options for all shipment types.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <Leaf className="h-10 w-10 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Green Routes</h3>
                <p className="opacity-80">Optimized routing to minimize fuel consumption and emissions.</p>
              </div>
            </div>
          </section>

          {/* Warehouse Image */}
          <section className="mb-20">
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img
                src={warehouse}
                alt="Costal Meridian eco-friendly warehouse"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="text-center mt-8">
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Our modern warehouses in California are designed with sustainability at their core, featuring solar power, rainwater harvesting, and energy-efficient systems.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <h2 className="text-3xl font-bold mb-4">Partner with Us for Sustainable Logistics</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join the growing number of businesses choosing environmentally responsible shipping from California.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Get a Green Quote
              </Button>
              <Button size="lg" variant="outline" asChild className="hover-scale">
                <NavLink to="/contact">Contact Our Sustainability Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default Sustainability;